import { useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import OnboardingProfileSearchableSelect from '../../components/OnboardingProfileSearchableSelect';
import StepPanel from '../../components/StepPanel';
import WizardFooter from '../../components/WizardFooter';
import Field from '../../components/ui/Field';
import PermissionsEditor from '../../components/permissions/PermissionsEditor';
import useProfileStep from './useProfileStep';

const emptyInviteForm = () => ({
  first_name: '',
  last_name: '',
  username: '',
  email: '',
  mobile: '',
  is_superuser: false,
});

function displayName(u) {
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || u.name || u.username;
}

export default function Users() {
  const {
    draft,
    setDraft,
    steps,
    stepErrors,
    currentStep,
    organisationId,
    profileId,
    sessionId,
    isSaving,
    createProfileNow,
    profileMeta,
    licenseProfile,
    permissionsSource,
    profileCreated,
    permissionsLoading,
    permissionsError,
    showPermissions,
    showCloneDropdown,
    updateProfileMeta,
    handleCloneProfileChange,
    resetToLicenseTemplate,
    enableManualTemplate,
  } = useProfileStep();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [invite, setInvite] = useState(emptyInviteForm);
  const [inviteErrors, setInviteErrors] = useState({});

  const stepStatus = steps.profile_users;
  const canAddUsers = profileCreated;
  const org = draft.organisation ?? {};

  const updateObject = (id, field, value) => {
    setDraft((prev) => ({
      ...prev,
      objectPermissions: prev.objectPermissions.map((p) =>
        p.id === id ? { ...p, [field]: value } : p
      ),
    }));
  };

  const updateGeneral = (id, value) => {
    setDraft((prev) => ({
      ...prev,
      generalPermissions: prev.generalPermissions.map((p) =>
        p.id === id ? { ...p, isAllowed: value } : p
      ),
    }));
  };

  const filtered = draft.users.filter((u) => {
    const q = search.toLowerCase();
    return (
      displayName(u).toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q) ||
      u.username.toLowerCase().includes(q) ||
      u.mobile.includes(search)
    );
  });

  const activeCount = draft.users.filter((u) => u.status === 'Active').length;
  const pendingCount = draft.users.filter((u) => u.status === 'Pending').length;

  const handleInvite = () => {
    const row = {
      id: crypto.randomUUID?.() ?? String(Date.now()),
      first_name: invite.first_name.trim(),
      last_name: invite.last_name.trim(),
      username: invite.username.trim(),
      email: invite.email.trim(),
      mobile: invite.mobile.replace(/\D/g, '').slice(0, 15),
      is_superuser: invite.is_superuser,
      status: 'Pending',
    };
    const errs = (() => {
      const phoneRegex = /^([1-9]{1}[0-9]{1,14})$/;
      const e = {};
      if (!row.first_name) e.first_name = 'First name is required';
      if (!row.last_name) e.last_name = 'Last name is required';
      if (!row.username) e.username = 'Username is required';
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(row.email)) e.email = 'Valid email is required';
      if (!phoneRegex.test(row.mobile)) e.mobile = 'Valid mobile number is required';
      return e;
    })();
    setInviteErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setDraft((prev) => ({ ...prev, users: [...prev.users, row] }));
    setInvite(emptyInviteForm());
    setInviteErrors({});
    setOpen(false);
  };

  return (
    <>
      <StepPanel stepId={4} stepStatus={stepStatus}>
        {(stepErrors.users ||
          stepErrors.profile ||
          stepErrors.organisation_id ||
          stepErrors.clone_from_profile_id ||
          stepErrors.profile_name ||
          stepErrors.objectPermissions ||
          stepErrors.generalPermissions) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {stepErrors.organisation_id ||
              stepErrors.profile ||
              stepErrors.users ||
              stepErrors.clone_from_profile_id ||
              stepErrors.profile_name ||
              stepErrors.objectPermissions ||
              stepErrors.generalPermissions}
          </Alert>
        )}

        {org.license_alias && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Plan: <strong>{org.license_alias}</strong>
            {licenseProfile ? (
              <>
                {' '}
                · Permission template: <strong>{licenseProfile.name}</strong> (from license)
              </>
            ) : (
              <> · No permission profile linked to this license — select a template below.</>
            )}
          </Alert>
        )}

        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Profile setup
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Permissions from your license plan are loaded automatically. Review and adjust them, then
          create the organisation profile before adding users.
          {organisationId ? ` Organisation: ${organisationId}` : ''}
        </Typography>
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {showCloneDropdown ? (
            <Grid item xs={12} sm={6}>
              <OnboardingProfileSearchableSelect
                label="Clone from profile"
                valueId={profileMeta.clone_from_profile_id}
                selectedProfile={
                  licenseProfile &&
                  String(licenseProfile.id) === String(profileMeta.clone_from_profile_id)
                    ? licenseProfile
                    : null
                }
                disabled={profileCreated}
                onChange={handleCloneProfileChange}
                error={!!stepErrors.clone_from_profile_id}
                helperText={stepErrors.clone_from_profile_id}
                placeholder="Search profiles..."
                noOptionsText="No profiles found"
              />
            </Grid>
          ) : (
            <Grid item xs={12} sm={6}>
              <Field
                label="Permission template"
                value={licenseProfile?.name || ''}
                disabled
                helperText="Loaded from the license selected in step 1"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <Field
              label="New profile name"
              required
              value={profileMeta.profile_name}
              onChange={(e) => updateProfileMeta('profile_name', e.target.value)}
              disabled={profileCreated}
              error={!!stepErrors.profile_name}
              helperText={stepErrors.profile_name}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>

        {!profileCreated && licenseProfile && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {showCloneDropdown ? (
              <Button size="small" onClick={resetToLicenseTemplate}>
                Reset to license template
              </Button>
            ) : (
              <Button size="small" onClick={enableManualTemplate}>
                Use a different template
              </Button>
            )}
            {permissionsSource === 'manual' && !showCloneDropdown && (
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                You switched away from the license default template.
              </Typography>
            )}
          </Box>
        )}

        {profileCreated && (
          <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
            Profile created (ID: {profileId}). You can now add users below; they will use this
            profile.
          </Alert>
        )}

        {!profileMeta.clone_from_profile_id && !profileCreated && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {org.license_id
              ? 'The selected license has no permission profile. Choose a template above or update the license in step 1.'
              : 'Select a license in step 1 or choose a profile template above to load permissions.'}
          </Alert>
        )}

        {profileMeta.clone_from_profile_id && permissionsLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 4, mb: 3 }}>
            <CircularProgress size={24} />
            <Typography color="text.secondary">
              Loading permissions from {licenseProfile?.name || 'selected profile'}…
            </Typography>
          </Box>
        )}

        {permissionsError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {permissionsError}
          </Alert>
        )}

        {showPermissions && (
          <>
            <PermissionsEditor
              objectPermissions={draft.objectPermissions}
              generalPermissions={draft.generalPermissions}
              disabled={permissionsLoading || profileCreated}
              onObjectChange={updateObject}
              onGeneralChange={updateGeneral}
            />

            {!profileCreated && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!sessionId || isSaving || permissionsLoading}
                  onClick={() => void createProfileNow()}
                >
                  {isSaving ? 'Creating profile…' : 'Create Profile'}
                </Button>
              </Box>
            )}
          </>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Add Users
        </Typography>
        {!canAddUsers && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Create the profile first (button above). Users are linked to that profile when you
            continue.
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <Chip label={`Total: ${draft.users.length}`} />
          <Chip label={`Active: ${activeCount}`} color="success" variant="outlined" />
          <Chip label={`Pending: ${pendingCount}`} color="warning" variant="outlined" />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Field
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            disabled={!canAddUsers}
            onClick={() => setOpen(true)}
          >
            Add User
          </Button>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary">
                      {canAddUsers
                        ? 'No users yet. Invite your first team member.'
                        : 'Create the profile above, then add users here.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{displayName(u)}</TableCell>
                    <TableCell>{u.username}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.mobile}</TableCell>
                    <TableCell>
                      <Chip
                        label={u.status}
                        size="small"
                        color={u.status === 'Active' ? 'success' : 'warning'}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </StepPanel>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
            This user will be assigned to profile ID{' '}
            {profileId ?? '(create profile first)'} and organisation{' '}
            {organisationId ?? '(from step 1)'} when you save and continue.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Field
                label="First Name"
                required
                value={invite.first_name}
                onChange={(e) => setInvite({ ...invite, first_name: e.target.value })}
                error={!!inviteErrors.first_name}
                helperText={inviteErrors.first_name}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                label="Last Name"
                required
                value={invite.last_name}
                onChange={(e) => setInvite({ ...invite, last_name: e.target.value })}
                error={!!inviteErrors.last_name}
                helperText={inviteErrors.last_name}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                label="Username"
                required
                value={invite.username}
                onChange={(e) => setInvite({ ...invite, username: e.target.value })}
                error={!!inviteErrors.username}
                helperText={inviteErrors.username}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                label="Email"
                required
                type="email"
                value={invite.email}
                onChange={(e) => setInvite({ ...invite, email: e.target.value })}
                error={!!inviteErrors.email}
                helperText={inviteErrors.email}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Field
                label="Mobile Number"
                required
                value={invite.mobile}
                onChange={(e) =>
                  setInvite({ ...invite, mobile: e.target.value.replace(/\D/g, '').slice(0, 15) })
                }
                error={!!inviteErrors.mobile}
                helperText={inviteErrors.mobile}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={invite.is_superuser}
                    onChange={(e) => setInvite({ ...invite, is_superuser: e.target.checked })}
                  />
                }
                label="Is Super User"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
              setInviteErrors({});
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleInvite}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <WizardFooter stepId={currentStep} />
    </>
  );
}
